import { useToast } from "@qubit/autoparts";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";

import useFlag from "~/config/flags";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useConfigurePortsMutation } from "~/redux/warehouse/workstation.hooks";

import { WorkstationMode } from "~/redux/warehouse/workstations.openApi";

const MAX_RETRIES = 10;
const RETRY_INTERVAL = 5000;

/**
 * Minimum time (ms) a configure-ports request may run before the "Preparing
 * Workstation" modal is shown. Keeps the modal from flashing on fast responses.
 */
const minimumWaitDurationBeforeOpeningModal = 300;

/**
 * Hook to enable/disable multiport mode with automatic retry on conflicts (409).
 * The retries are necessary to wait for the ports to be flushed and ready for
 * the next operation.
 *
 * @param workstationMode - the workstation mode to configure the ports for.
 * @param retryThreshold - how long (ms) the request may run before
 *   `isConfigurePortsRetrying` flips true. Defaults to
 *   `minimumWaitDurationBeforeOpeningModal`; Picking passes a longer value.
 */
export const useConfigurePortsWithRetry = ({
  workstationMode: workstationMode,
  retryThreshold = minimumWaitDurationBeforeOpeningModal
}: {
  workstationMode: WorkstationMode;
  retryThreshold?: number;
}) => {
  const { portModeFlushingEnabled } = useFlag();
  const { errorToast } = useToast();
  const { t } = useTranslation();

  const [isRetrying, setIsRetrying] = useState(false);
  const [elapsedDuration, setElapsedDuration] = useState(0);

  const startTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const [configurePorts] = useConfigurePortsMutation();

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const reset = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startTimeRef.current = null;
    setElapsedDuration(0);
    setIsRetrying(false);
  };

  const startTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    startTimeRef.current = Date.now();
    setElapsedDuration(0);

    intervalRef.current = setInterval(() => {
      if (startTimeRef.current !== null) {
        setElapsedDuration(Date.now() - startTimeRef.current);
      }
    }, 100);
  };

  const configurePortsWithRetry = async (attempt = 0) => {
    if (!portModeFlushingEnabled || !siteWorkstation) {
      return true;
    }

    if (attempt === 0) {
      startTimer();
    }

    try {
      await configurePorts({
        autostoreGridId: siteWorkstation?.autostoreGridId,
        workstationId: siteWorkstation?.id,
        body: { workstationMode: workstationMode }
      }).unwrap();

      // If we made it here, configurePorts was successful.  Exit successfully.
      reset();
      return true;
    } catch (error) {
      // Retry logic
      if (
        (error as FetchBaseQueryError)?.status === 409 &&
        attempt < MAX_RETRIES
      ) {
        setIsRetrying(true);
        await new Promise((resolve) => setTimeout(resolve, RETRY_INTERVAL));
        return await configurePortsWithRetry(attempt + 1);
      } else {
        // Something went wrong
        reset();
        errorToast(t("unable to prepare workstation"));
        return false;
      }
    }
  };

  return {
    configurePortsWithRetry: configurePortsWithRetry,
    isConfigurePortsRetrying: isRetrying || elapsedDuration > retryThreshold,
    multiPortRequestDuration: elapsedDuration
  };
};
